import React, { createContext, useContext, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// Note: Virtuoso is imported directly in ChatWindow.jsx, not here
import { db, functions } from '../../../firebaseConfig';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNotificationTriggers } from '../../notifications';
import { messageQueue } from '../services/messageQueue';
import { mergeMessagesWithQueue, sortMessagesByTime } from '../utils/messageUtils';
import {
  setMessages,
  setHasMore,
  setLastDoc,
  setHighlightedMessage,
} from '../../../redux/slices/chatSlice';
import { useConversation } from './ConversationProvider';


/**
 * Message Context
 * Handles message loading, pagination, sending, and virtualized list management
 */
const MessageContext = createContext(null);

export { MessageContext };

export const useMessages = () => {
  const ctx = useContext(MessageContext);
  if (!ctx) {
    throw new Error('useMessages must be used within MessageProvider');
  }
  return ctx;
};

// VirtuosoMessageList was moved to ChatWindow.jsx where Virtuoso is already imported

export const MessageProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { triggerMentionNotification } = useNotificationTriggers();
  const { updateConversationOptimistic } = useConversation();

  // Redux Selectors
  const currentUser = useSelector(state => state.auth.currentUser);
  const activeConversationId = useSelector(state => state.chat.activeConversationId);
  const messagesByConversation = useSelector(state => state.chat.messagesByConversation);
  const hasMoreByConversation = useSelector(state => state.chat.hasMoreByConversation);
  const lastDocsByConversation = useSelector(state => state.chat.lastDocsByConversation);

  // Local state
  const [loadingMore, setLoadingMore] = useState(false);

  // Refs for virtuoso - this ref is passed to context for scroll-to-bottom functionality
  const virtuosoRef = useRef(null);

  // Ref to always access latest messagesByConversation (fixes stale closure in editMessage rollback & sendMessage)
  const messagesByConvRef = useRef(messagesByConversation);
  messagesByConvRef.current = messagesByConversation;

  // FIX C-5: Track ALL pending timeouts in a Set (not just the last one)
  const removeTimeoutsRef = useRef(new Set());

  // Get messages for current conversation
  const messages = useMemo(
    () => messagesByConversation[activeConversationId] || [],
    [messagesByConversation, activeConversationId]
  );

  const hasMore = hasMoreByConversation[activeConversationId] ?? false;

  // Subscribe to messages for active conversation
  useEffect(() => {
    if (!activeConversationId) return undefined;

    // FIX 1.1: Cancellation flag prevents stale listener callbacks from dispatching
    // messages from the wrong conversation when switching rapidly
    let isCancelled = false;

    // FIX C-2: Track queue subscription OUTSIDE onSnapshot to prevent leak accumulation.
    // onSnapshot's return value is ignored by Firestore — we must manage it ourselves.
    let queueUnsub = null;

    // Delivered-marking: track which messageIds this listener has already submitted so
    // repeated snapshot re-fires (cache → server, plus subsequent updates) don't re-call
    // the cloud function. The Set lives for the conversation's listener lifetime.
    const deliveredMarked = new Set();

    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', activeConversationId),
      orderBy('createdAt', 'desc'),
      limit(30)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (isCancelled) return; // Ignore callbacks after cancellation

      const items = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null
        };
      }).reverse();

      // Helper to merge and dispatch messages
      const updateMessages = () => {
        const queuedMsgs = messageQueue.getByConversation(activeConversationId);
        const msgsToShow = mergeMessagesWithQueue(items, queuedMsgs, (queueId) => {
          messageQueue.remove(queueId);
        });
        const finalMessages = sortMessagesByTime(msgsToShow);
        dispatch(setMessages({ conversationId: activeConversationId, messages: finalMessages }));
      };

      // Initial update from snapshot
      updateMessages();

      // FIX Phase 1 (delivered status): Mark undelivered messages as delivered.
      // Runs inside onSnapshot so it fires exactly when messages arrive (closes the
      // first-open race) and skips the recipient-redundant optimistic Redux dispatch
      // (the recipient never renders their own deliveredTo — Firestore's next snapshot
      // is the single source of truth for the sender's ticks).
      if (currentUser?.uid) {
        const undelivered = items
          .filter(m =>
            m.senderId !== currentUser.uid &&
            m.createdAt && // Only deliver messages with timestamps
            !m.deliveredTo?.[currentUser.uid] &&
            !deliveredMarked.has(m.id)
          )
          .map(m => m.id)
          .slice(0, 100); // Limit to 100 per call for performance

        if (undelivered.length > 0) {
          undelivered.forEach(id => deliveredMarked.add(id));
          const callable = httpsCallable(functions, 'markMessagesDelivered');
          callable({ conversationId: activeConversationId, messageIds: undelivered })
            .catch(err => {
              console.error('[Message] markMessagesDelivered error', err);
              // On failure, untrack so a later snapshot can retry
              undelivered.forEach(id => deliveredMarked.delete(id));
            });
        }
      }

      // FIX C-2: Clean up previous queue subscription before creating new one
      if (queueUnsub) queueUnsub();
      queueUnsub = messageQueue.subscribe(() => {
        if (!isCancelled) updateMessages();
      });

      if (snapshot.docs.length > 0) {
        dispatch(setLastDoc({
          conversationId: activeConversationId,
          lastDoc: snapshot.docs[snapshot.docs.length - 1]
        }));
      }

      dispatch(setHasMore({
        conversationId: activeConversationId,
        hasMore: snapshot.docs.length === 30
      }));
    }, (error) => {
      if (isCancelled) return;
      console.error('[Message] listener error', error);
    });

    return () => {
      isCancelled = true;
      unsubscribe();
      // FIX C-2: Clean up queue subscription on unmount
      if (queueUnsub) queueUnsub();
    };
  }, [activeConversationId, currentUser?.uid, dispatch]);

  // Mark conversation as seen when it becomes active or new messages arrive
  useEffect(() => {
    if (!activeConversationId || !currentUser?.uid) return;

    const convRef = doc(db, 'conversations', activeConversationId);
    getDoc(convRef).then((convSnap) => {
      if (!convSnap.exists()) return;
      const conv = convSnap.data();
      const lmaRaw = conv.lastMessageAt;
      const lma = lmaRaw?.toDate ? lmaRaw.toDate() : lmaRaw || null;
      const lsaRaw = conv.lastSeenAt ? conv.lastSeenAt[currentUser.uid] : null;
      const lsa = lsaRaw?.toDate ? lsaRaw.toDate() : lsaRaw || null;

      const hasUnread = lma && (!lsa || lma > lsa);
      if (hasUnread) {
        // Optimistically update the UI to instantly clear the unread badge
        updateConversationOptimistic(activeConversationId, {
          lastSeenAt: {
            [currentUser.uid]: new Date()
          }
        });

        // FIX C-3: Actually call the Cloud Function instead of dispatching a no-op action
        const callable = httpsCallable(functions, 'markConversationSeen');
        callable({ conversationId: activeConversationId }).catch(err => {
          console.error('[Message] markConversationSeen error', err);
        });
      }
    });
  }, [activeConversationId, currentUser?.uid, dispatch, updateConversationOptimistic]);


  // Scroll to highlighted message
  const scrollToMessage = useCallback((messageId) => {
    if (!messageId) return;
    dispatch(setHighlightedMessage(messageId));
    setTimeout(() => {
      dispatch(setHighlightedMessage(null));
    }, 2000);
  }, [dispatch]);

  // Delete message
  const deleteMessage = useCallback(async (messageId) => {
    if (!currentUser?.uid || !activeConversationId || !messageId) return;
    try {
      const callable = httpsCallable(functions, 'deleteChatMessage');
      await callable({ conversationId: activeConversationId, messageId });
    } catch (error) {
      console.error('[Message] deleteMessage error', error);
    }
  }, [currentUser?.uid, activeConversationId]);

  // Edit message with optimistic update
  const editMessage = useCallback(async (messageId, newText, originalContent = null) => {
    if (!currentUser?.uid || !activeConversationId || !messageId || !newText?.trim()) {
      return Promise.reject(new Error('Invalid edit parameters'));
    }

    // Optimistic update
    // FIX H-2: Use ref for fresh state to prevent stale closure in rapid edits
    const msgs = messagesByConvRef.current[activeConversationId] || [];
    const msgIndex = msgs.findIndex(m => m.id === messageId);
    if (msgIndex !== -1) {
      const updatedMsgs = [...msgs];
      updatedMsgs[msgIndex] = {
        ...updatedMsgs[msgIndex],
        content: newText,
        edited: true,
        editedAt: new Date(),
        isOptimistic: true
      };
      dispatch(setMessages({ conversationId: activeConversationId, messages: updatedMsgs }));
    }

    try {
      const callable = httpsCallable(functions, 'editChatMessage');
      await callable({ conversationId: activeConversationId, messageId, newText });
    } catch (error) {
      // FIX 4.1: Rollback uses FRESH state via ref to prevent overwriting messages
      // that arrived while the edit API call was in-flight
      if (originalContent) {
        const freshMsgs = messagesByConvRef.current[activeConversationId] || [];
        const freshIndex = freshMsgs.findIndex(m => m.id === messageId);
        if (freshIndex !== -1) {
          const updatedMsgs = [...freshMsgs];
          updatedMsgs[freshIndex] = {
            ...updatedMsgs[freshIndex],
            content: originalContent,
            edited: updatedMsgs[freshIndex].editedBeforeOptimistic || false,
            isOptimistic: false,
          };
          dispatch(setMessages({ conversationId: activeConversationId, messages: updatedMsgs }));
        }
      }
      console.error('[Message] editMessage error', error);
      throw error;
    }
  }, [currentUser?.uid, activeConversationId, dispatch]);

  // Send message with optimistic update
  const sendMessage = useCallback((
    {
      conversationId,
      content,
      imageUrl = null,
      fileUrl = null,
      fileName = null,
      fileType = null,
      fileSize = null,
      mentionedUserIds = [],
      replyTo = null,
      sharedPost = null,
      fireAndForget = false,
    }
  ) => {
    if (!currentUser?.uid || !conversationId) {
      return Promise.reject(new Error('Invalid send parameters'));
    }
    if (!content?.trim() && !imageUrl && !fileUrl && !sharedPost) {
      return Promise.reject(new Error('Message must have content or attachment'));
    }

    // Create optimistic message with tempId for stable key/deduplication
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add to persistent queue with all display fields
    // The queue is the SINGLE SOURCE OF TRUTH for optimistic messages
    const queueMessageId = messageQueue.add({
      conversationId,
      senderId: currentUser.uid,
      content: content || null,
      imageUrl: imageUrl || null,
      fileUrl: fileUrl || null,
      fileName: fileName || null,
      fileType: fileType || null,
      fileSize: fileSize || null,
      createdAt: Date.now(),
      mentionedUserIds: Array.isArray(mentionedUserIds) ? mentionedUserIds : [],
      replyTo,
      sharedPost,
      linkInfo: { hasLinks: false, risks: [] },
      tempId, // Used for deduplication with Firestore message
      // Display fields for rendering
      id: tempId, // Use tempId as ID for consistent React key
      isOptimistic: true,
      status: 'sending',
    });

    // FIX 4.2: Use ref for fresh state to prevent two rapid sends from
    // overwiting each other's optimistic messages
    const currentMessages = messagesByConvRef.current[conversationId] || [];
    const queuedMsgs = messageQueue.getByConversation(conversationId);
    const updatedMessages = sortMessagesByTime([...currentMessages, ...queuedMsgs]);
    dispatch(setMessages({ conversationId, messages: updatedMessages }));

    // FIX Issue 7: Instantly update the Conversation List item so it jumps to the top
    // without waiting 1-3 seconds for Firestore onSnapshot network roundtrip
    // Format the preview string just like the backend does
    let lastMessagePreview = content || '';
    if (!lastMessagePreview) {
      if (imageUrl) lastMessagePreview = '[image]';
      else if (fileUrl) lastMessagePreview = '[file]';
      else if (sharedPost) lastMessagePreview = 'Shared a post';
    }
    
    // Convert to mock Firestore timestamp shape equivalent to what the UI hook expects
    // For Redux slices dealing with dates, we just pass the Date object which gets parsed
    const now = new Date();
    
    // Optimistic UI bump
    updateConversationOptimistic(conversationId, {
      lastMessageAt: now,
      lastMessageText: lastMessagePreview.substring(0, 200),
      lastMessageSenderId: currentUser.uid,
      // Temporarily use current displayName or username
      lastMessageSenderName: currentUser.displayName || currentUser.username || 'You',
      lastSeenAt: {
        [currentUser.uid]: now
      }
    });

    // Trigger mention notifications
    if (mentionedUserIds && mentionedUserIds.length > 0) {
      triggerMentionNotification(
        mentionedUserIds.filter(id => id !== currentUser.uid),
        {
          conversationId,
          type: 'chat',
          text: content?.substring(0, 100) || ''
        }
      ).catch(e => console.warn('[Message] Mention notif failed', e));
    }

    // Process queue item
    const processQueueItem = async () => {
      try {
        messageQueue.updateStatus(queueMessageId, 'sending');

        const callable = httpsCallable(functions, 'sendChatMessage');
        const res = await callable({
          conversationId,
          content,
          imageUrl,
          fileUrl,
          fileName,
          fileType,
          fileSize,
          mentionedUserIds,
          replyTo,
          sharedPost,
          tempId, // Pass tempId for stable key/deduplication
        });

        messageQueue.update(queueMessageId, {
          status: 'sent',
          realId: res.data.messageId,
          sentAt: Date.now()
        });

        // FIX C-5: Track ALL timeouts in a Set so none are missed on unmount
        const timeoutId = setTimeout(() => {
          messageQueue.remove(queueMessageId);
          removeTimeoutsRef.current.delete(timeoutId);
        }, 10000);
        removeTimeoutsRef.current.add(timeoutId);
      } catch (error) {
        console.error('[Message] Background send failed', error);
        messageQueue.updateStatus(queueMessageId, 'failed');
      }
    };

    // FIX 2.1: Add .catch() to prevent unhandled promise rejection
    processQueueItem().catch(err => {
      console.error('[Message] processQueueItem unhandled error', err);
    });

    if (fireAndForget) {
      return Promise.resolve({ success: true, queued: true, messageId: queueMessageId });
    }

    return Promise.resolve({ success: true, messageId: tempId });
  }, [
    currentUser?.uid, 
    currentUser?.displayName, 
    currentUser?.username, 
    dispatch, 
    triggerMentionNotification, 
    updateConversationOptimistic
  ]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!activeConversationId || loadingMore || !hasMoreByConversation[activeConversationId]) return;

    // FIX M-15: lastDoc is now a serialized Timestamp cursor, not a DocumentSnapshot
    const lastCursor = lastDocsByConversation[activeConversationId];
    if (!lastCursor) return;

    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'messages'),
        where('conversationId', '==', activeConversationId),
        orderBy('createdAt', 'desc'),
        startAfter(lastCursor), // FIX M-15: Firestore accepts raw Timestamp values
        limit(30)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const newItems = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null
          };
        }).reverse();

        // FIX H-10: Use ref for fresh state to prevent stale closure during pagination
        const currentMessages = messagesByConvRef.current[activeConversationId] || [];
        dispatch(setMessages({
          conversationId: activeConversationId,
          messages: [...newItems, ...currentMessages]
        }));

        dispatch(setLastDoc({
          conversationId: activeConversationId,
          lastDoc: snapshot.docs[snapshot.docs.length - 1]
        }));

        dispatch(setHasMore({
          conversationId: activeConversationId,
          hasMore: snapshot.docs.length === 30
        }));
      } else {
        dispatch(setHasMore({
          conversationId: activeConversationId,
          hasMore: false
        }));
      }
    } catch (error) {
      console.error('[Message] loadMoreMessages error', error);
    } finally {
      setLoadingMore(false);
    }
  }, [activeConversationId, loadingMore, hasMoreByConversation, lastDocsByConversation, dispatch]);

  // FIX 8.2: Use ref-based lookup so getMessage identity is stable and doesn't
  // trigger re-renders in all consumers every time messages change
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Scroll to bottom
  // FIX A2: Use 'auto' (instant) and 'LAST' index. 'smooth' undershoots with
  // lazily-measured dynamic heights (images, shared-post cards, conditional meta),
  // leaving the last message partially clipped. Instant scroll is pixel-accurate.
  // 'LAST' avoids off-by-one between messages and displayMessages in search mode.
  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({
      index: 'LAST',
      align: 'end',
      behavior: 'auto',
    });
  }, []);

  const getMessage = useCallback((id) => messagesRef.current.find(m => m.id === id), []);

  // FIX C-5: Cleanup ALL tracked timeouts on unmount
  useEffect(() => {
    const timeouts = removeTimeoutsRef.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const value = useMemo(() => ({
    // State
    messages,
    loadingMore,
    hasMore,

    // Actions
    sendMessage,
    loadMoreMessages,
    deleteMessage,
    editMessage,
    scrollToMessage,
    scrollToBottom,

    // Refs
    virtuosoRef,

    // Helpers
    getMessage,
  }), [
    messages,
    loadingMore,
    hasMore,
    sendMessage,
    loadMoreMessages,
    deleteMessage,
    editMessage,
    scrollToMessage,
    scrollToBottom,
    getMessage,
  ]);

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
};

export default MessageProvider;
